// finlytics-frontend/src/pages/dashboards/HormuzWatchPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts';
import apiClient from '../../services/apiClient';
import styles from './HormuzWatchPage.module.css';

// ---------------------------------------------------------------------------
// Hardcoded fallback data — used when MongoDB collections are empty or API fails
// ---------------------------------------------------------------------------

const FALLBACK_PRICE_DATA = [
    { date: "Jan 3", price: 74.2 },
    { date: "Jan 6", price: 73.8 },
    { date: "Jan 9", price: 75.1 },
    { date: "Jan 13", price: 74.6 },
    { date: "Jan 16", price: 76.2 },
    { date: "Jan 20", price: 75.8 },
    { date: "Jan 23", price: 77.3 },
    { date: "Jan 27", price: 76.9 },
    { date: "Jan 30", price: 78.1 },
    { date: "Feb 3", price: 77.6 },
    { date: "Feb 6", price: 79.2 },
    { date: "Feb 10", price: 78.8 },
    { date: "Feb 13", price: 80.4 },
    { date: "Feb 17", price: 79.9 },
    { date: "Feb 20", price: 81.5 },
    { date: "Feb 24", price: 80.7 },
    { date: "Feb 27", price: 81.2 },
    { date: "Feb 28", price: 88.4 },
    { date: "Mar 3", price: 94.1 },
    { date: "Mar 7", price: 91.8 },
    { date: "Mar 10", price: 95.3 },
    { date: "Mar 14", price: 98.6 },
    { date: "Mar 17", price: 97.2 },
    { date: "Mar 21", price: 101.4 },
    { date: "Mar 24", price: 103.8 },
    { date: "Mar 26", price: 105.9 },
    { date: "Mar 27", price: 107.8 },
    { date: "Mar 28", price: 112.6 },
    { date: "Mar 29", price: 108.5 },
    { date: "Mar 30", price: 114.9 },
];

const FALLBACK_TRAFFIC_DATA = [
    { period: "Pre-War Avg", vessels: 135, status: "normal" },
    { period: "Mar 1–7", vessels: 3, status: "critical" },
    { period: "Mar 8–14", vessels: 2, status: "critical" },
    { period: "Mar 15–21", vessels: 3, status: "critical" },
    { period: "Mar 22–26", vessels: 4, status: "critical" },
    { period: "Mar 27–28", vessels: 8, status: "restricted" },
    { period: "Mar 29–30", vessels: 10, status: "restricted" },
];

const TIMELINE_EVENTS = [
    { date: "Feb 4, 2025", severity: "diplomatic", title: "US–Israel pressure campaign begins", description: "US and Israel jointly pressure Iran over nuclear enrichment acceleration." },
    { date: "Jun 12, 2025", severity: "military", title: "Iran crosses 90% enrichment threshold", description: "IAEA confirms Iran has enriched uranium to 90% — weapons-grade." },
    { date: "Sep 3, 2025", severity: "warning", title: "US carrier group deployed to Gulf", description: "USS Gerald R. Ford CSG repositioned to Arabian Sea." },
    { date: "Nov 18, 2025", severity: "diplomatic", title: "Nuclear talks collapse in Vienna", description: "Iran walks out of EU-brokered nuclear negotiations." },
    { date: "Jan 14, 2026", severity: "warning", title: "Israel strikes Fordow facility", description: "Israeli Air Force strikes Fordow underground enrichment site." },
    { date: "Feb 4, 2026", severity: "critical", title: "Iran closes Strait of Hormuz — warning shots", description: "IRGC fires warning shots at US destroyer USS Cole in Strait of Hormuz." },
    { date: "Feb 20, 2026", severity: "military", title: "US–Israel joint strike planning confirmed", description: "Pentagon confirms joint operational planning with IDF for Iranian targets." },
    { date: "Feb 28, 2026", severity: "critical", title: "Operation Epic Fury — US strikes begin", description: "US launches coordinated strikes on Iranian nuclear sites, air defenses, IRGC command. Brent surges $7 in hours." },
    { date: "Mar 1, 2026", severity: "critical", title: "Iran fully blockades Strait of Hormuz", description: "IRGC deploys mines and naval assets. All commercial tanker traffic halts. Brent crosses $90." },
    { date: "Mar 2, 2026", severity: "military", title: "IRGC seizes 3 oil tankers", description: "IRGC seizes Greek-owned tankers in retaliation. Lloyd's of London suspends Hormuz war-risk cover." },
    { date: "Mar 5, 2026", severity: "economic", title: "IEA triggers emergency oil reserves release", description: "IEA coordinates 60M barrel strategic reserve release across member states." },
    { date: "Mar 7, 2026", severity: "military", title: "US Navy mine countermeasures deployed", description: "USS Devastator and USS Scout deployed for mine-clearing operations." },
    { date: "Mar 10, 2026", severity: "economic", title: "Saudi Arabia pledges spare capacity surge", description: "Saudi Aramco commits to +2 mb/d surge through Red Sea routing." },
    { date: "Mar 14, 2026", severity: "military", title: "Iran retaliates — Houthi missile barrage on Israel", description: "Iran-backed Houthis fire 40+ ballistic missiles toward Israeli cities." },
    { date: "Mar 17, 2026", severity: "diplomatic", title: "UN Security Council emergency session", description: "UNSC meets — Russia and China veto ceasefire resolution." },
    { date: "Mar 19, 2026", severity: "warning", title: "Brent crosses $100 — market panic", description: "Brent crude crosses $100/bbl for first time since 2022 energy crisis." },
    { date: "Mar 21, 2026", severity: "economic", title: "India activates strategic petroleum reserve", description: "India releases 5M barrels from Mangalore, Padur, Visakhapatnam SPR facilities." },
    { date: "Mar 24, 2026", severity: "diplomatic", title: "China mediates — indirect US–Iran backchannel opens", description: "China's foreign ministry confirms backchannel communication facilitated through Beijing." },
    { date: "Mar 26, 2026", severity: "economic", title: "IRGC formalizes yuan toll booth", description: "IRGC announces official transit fee of ~$2M per vessel crossing, payable in yuan or gold." },
    { date: "Mar 27, 2026", severity: "military", title: "IRGC opens tolled passage — traffic trickles", description: "First 8 vessels transit under IRGC escort. Houthis separately attack Israeli port of Eilat." },
    { date: "Mar 28, 2026", severity: "critical", title: "Brent surges to $112 — WTI crosses $100", description: "Oil markets spike as Pakistan–Iran deal amplifies IRGC control narrative." },
    { date: "Mar 28, 2026", severity: "diplomatic", title: "Pakistan secures Hormuz deal for 20 ships", description: "Pakistan negotiates passage for 20 vessels through Hormuz under IRGC escort arrangement." },
    { date: "Mar 29, 2026", severity: "diplomatic", title: "Iran dismisses US diplomatic overtures", description: "Iran's foreign ministry rejects Pakistan-facilitated ceasefire proposal." },
    { date: "Mar 30, 2026", severity: "critical", title: "Houthis open 2nd front at Bab al-Mandeb", description: "Houthis blockade Bab al-Mandeb strait — now both Red Sea entry points disrupted." },
    { date: "Mar 30, 2026", severity: "warning", title: "Trump threatens Iranian energy infrastructure", description: "Trump posts threat to strike Iranian oil terminals and refineries if Hormuz not reopened within 72 hours." },
    { date: "Mar 30, 2026", severity: "critical", title: "Brent hits $114.88 — record monthly gain", description: "Brent closes at $114.88 — +47.77% in March, largest monthly gain since COVID recovery in 2021." },
];

// ---------------------------------------------------------------------------
// Severity badge styling
// ---------------------------------------------------------------------------
const SEVERITY_COLORS = {
    critical: '#ef4444',
    military: '#3b82f6',
    warning: '#f59e0b',
    diplomatic: '#14b8a6',
    economic: '#a855f7',
};

function getSeverityStyle(severity) {
    const color = SEVERITY_COLORS[severity] || '#6b7280';
    return {
        background: `${color}22`,
        color,
        border: `1px solid ${color}55`,
    };
}

// ---------------------------------------------------------------------------
// Traffic bar color
// ---------------------------------------------------------------------------
function getBarColor(status) {
    if (status === 'normal') return '#22c55e';
    if (status === 'critical') return '#ef4444';
    if (status === 'restricted') return '#eab308';
    return '#6b7280';
}

// ---------------------------------------------------------------------------
// Custom Tooltip for Brent Price Chart
// ---------------------------------------------------------------------------
function BrentTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div style={{
            background: '#1f2937',
            border: '1px solid #374151',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 13,
        }}>
            <p style={{ color: '#9ca3af', margin: '0 0 4px 0' }}>{label}</p>
            <p style={{ color: '#f59e0b', margin: 0, fontWeight: 700 }}>
                ${payload[0].value.toFixed(2)}
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Custom Tooltip for Traffic Chart
// ---------------------------------------------------------------------------
function TrafficTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const entry = payload[0].payload;
    return (
        <div style={{
            background: '#1f2937',
            border: '1px solid #374151',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 13,
        }}>
            <p style={{ color: '#9ca3af', margin: '0 0 4px 0' }}>{label}</p>
            <p style={{ color: getBarColor(entry.status), margin: '0 0 4px 0', fontWeight: 700 }}>
                {entry.vessels} vessels/day
            </p>
            <p style={{ color: '#6b7280', margin: 0, textTransform: 'capitalize' }}>
                {entry.status}
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Skeleton loader shown during API fetch
// ---------------------------------------------------------------------------
function LoadingSkeleton() {
    return (
        <div style={{ padding: 24 }}>
            {[1, 2, 3].map((i) => (
                <div
                    key={i}
                    className={styles.skeletonBlock}
                    style={{ height: i === 2 ? 280 : 120 }}
                />
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const HormuzWatchPage = () => {
    const navigate = useNavigate();

    // API state
    const [loading, setLoading] = useState(true);
    const [brentPrice, setBrentPrice] = useState(114.88);
    const [brentChange, setBrentChange] = useState('+2.05%');
    const [priceHistory, setPriceHistory] = useState(FALLBACK_PRICE_DATA);
    const [latestNews, setLatestNews] = useState([]);
    const [traffic, setTraffic] = useState({ vessels_per_day: 6, ships_stranded: 2000, capacity_pct: 4.4, date: '2026-03-30' });
    const [lastUpdated, setLastUpdated] = useState('Mar 30, 2026');

    // Macro indicator state
    const [macroIndicators, setMacroIndicators] = useState({});

    // Wiki timeline state (replaces hardcoded TIMELINE_EVENTS when available)
    const [wikiTimeline, setWikiTimeline] = useState([]);

    // Daily digest — one LLM summary per day
    const [dailyDigest, setDailyDigest] = useState([]);

    // Sync state
    const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');

    // Timeline filter
    const [activeFilter, setActiveFilter] = useState('all');

    const FILTER_OPTIONS = ['all', 'critical', 'military', 'diplomatic', 'economic', 'warning'];

    // ---------------------------------------------------------------------------
    // Fetch snapshot from API
    // ---------------------------------------------------------------------------
    const fetchSnapshot = useCallback(async () => {
        try {
            const { data } = await apiClient.get('/finlytics/hormuz/snapshot');

            if (data.brentPrice) setBrentPrice(data.brentPrice);
            if (data.brentChange) setBrentChange(data.brentChange);
            if (data.priceHistory && data.priceHistory.length > 0) {
                setPriceHistory(data.priceHistory);
            }
            if (data.latestNews && data.latestNews.length > 0) {
                setLatestNews(data.latestNews);
            }
            if (data.traffic) {
                setTraffic(data.traffic);
            }
            if (data.macroIndicators) {
                setMacroIndicators(data.macroIndicators);
            }
            if (data.wikiTimeline && data.wikiTimeline.length > 0) {
                setWikiTimeline(data.wikiTimeline);
            }
            if (data.dailyDigest && data.dailyDigest.length > 0) {
                setDailyDigest(data.dailyDigest);
            }
            if (data.lastUpdated) {
                try {
                    const d = new Date(data.lastUpdated);
                    setLastUpdated(
                        d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        + ' '
                        + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC', hour12: false })
                        + ' UTC'
                    );
                } catch {
                    setLastUpdated(data.lastUpdated);
                }
            }
        } catch (err) {
            console.warn('HormuzWatchPage: API fetch failed, using fallback data.', err?.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // ---------------------------------------------------------------------------
    // Trigger both sync HTTP functions then re-fetch snapshot
    // ---------------------------------------------------------------------------
    const syncNow = useCallback(async () => {
        setSyncing(true);
        setSyncMsg('Syncing price & macro data…');
        try {
            await apiClient.get('/hormuz_price_sync_http');
            setSyncMsg('Syncing news & traffic…');
            await apiClient.get('/hormuz_news_sync_http');
            setSyncMsg('Syncing Wikipedia timeline…');
            try { await apiClient.get('/hormuz_wiki_sync_http'); } catch { /* non-fatal */ }
            setSyncMsg('Refreshing dashboard…');
            await fetchSnapshot();
            setSyncMsg('Synced!');
        } catch (err) {
            setSyncMsg(`Sync failed: ${err?.message || 'unknown error'}`);
        } finally {
            setSyncing(false);
            setTimeout(() => setSyncMsg(''), 4000);
        }
    }, [fetchSnapshot]);

    useEffect(() => {
        fetchSnapshot();
    }, [fetchSnapshot]);

    // ---------------------------------------------------------------------------
    // Derived values
    // ---------------------------------------------------------------------------
    const capacityPct = traffic?.capacity_pct ?? 4.4;
    const vesselsPerDay = traffic?.vessels_per_day ?? 6;
    const shipsStranded = traffic?.ships_stranded ?? 2000;

    // Use wiki timeline from API if available, otherwise fall back to hardcoded
    const timelineEvents = wikiTimeline.length > 0
        ? wikiTimeline.map(ev => ({
            date: ev.date ? new Date(ev.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ev.date,
            severity: ev.severity || 'warning',
            title: ev.title || '',
            description: ev.description || '',
        }))
        : TIMELINE_EVENTS;

    const filteredEvents = activeFilter === 'all'
        ? timelineEvents
        : timelineEvents.filter((e) => e.severity === activeFilter);

    const priceChartData = priceHistory.map((d, i) => ({
        ...d,
        displayDate: i % 5 === 0 ? d.date : '',
    }));

    // ---------------------------------------------------------------------------
    // KPI card definitions — includes live macro data from FRED
    // ---------------------------------------------------------------------------
    const gold = macroIndicators.gold;
    const natGas = macroIndicators.natural_gas;
    const vix = macroIndicators.vix;
    const usdIdx = macroIndicators.usd_index;

    const KPI_CARDS = [
        {
            label: 'Brent Crude',
            value: `$${brentPrice.toFixed(2)}`,
            sub: `${brentChange} today`,
            accent: '#f59e0b',
            pulse: true,
        },
        {
            label: 'Gold (USD/oz)',
            value: gold ? `$${gold.value.toLocaleString()}` : '–',
            sub: gold ? `${gold.change} today` : 'sync to load',
            accent: '#eab308',
            pulse: false,
        },
        {
            label: 'Natural Gas',
            value: natGas ? `$${natGas.value.toFixed(2)}/MMBtu` : '–',
            sub: natGas ? `${natGas.change} today` : 'sync to load',
            accent: '#22c55e',
            pulse: false,
        },
        {
            label: 'VIX (Fear Index)',
            value: vix ? vix.value.toFixed(1) : '–',
            sub: vix ? `${vix.change} today` : 'sync to load',
            accent: '#ef4444',
            pulse: false,
        },
        {
            label: 'Hormuz Traffic',
            value: `${vesselsPerDay} vessels/day`,
            sub: 'vs 135 pre-war',
            accent: '#ef4444',
            pulse: false,
        },
        {
            label: 'Ships Stranded',
            value: `~${shipsStranded.toLocaleString()}`,
            sub: 'at Hormuz',
            accent: '#ef4444',
            pulse: false,
        },
        {
            label: 'USD Index',
            value: usdIdx ? usdIdx.value.toFixed(1) : 'N/A',
            sub: usdIdx ? `${usdIdx.change} today` : 'trade-weighted',
            accent: '#3b82f6',
            pulse: false,
        },
        {
            label: 'Hormuz Toll',
            value: '~$2M/crossing',
            sub: 'yuan-denominated',
            accent: '#a855f7',
            pulse: false,
        },
    ];

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    return (
        <div className={styles.page}>
            {/* ---------------------------------------------------------------- */}
            {/* Header                                                           */}
            {/* ---------------------------------------------------------------- */}
            <div className={styles.header}>
                <button
                    className={styles.backBtn}
                    onClick={() => navigate('/dashboards')}
                    aria-label="Dashboards"
                >
                    ← Dashboards
                </button>
                <div className={styles.headerText}>
                    <h1 className={styles.headerTitle}>HORMUZ WATCH</h1>
                    <p className={styles.headerSubtitle}>Iran–US Conflict Energy Dashboard</p>
                </div>
                <div className={styles.headerRight}>
                    <span className={styles.lastUpdated}>Last updated: {lastUpdated}</span>
                    <button
                        className={styles.syncBtn}
                        onClick={syncNow}
                        disabled={syncing || loading}
                        aria-label="Sync latest data"
                    >
                        {syncing ? '⟳ Syncing…' : '⟳ Sync Now'}
                    </button>
                    {syncMsg && (
                        <span className={styles.syncMsg}>{syncMsg}</span>
                    )}
                </div>
            </div>

            {/* ---------------------------------------------------------------- */}
            {/* Loading skeleton                                                 */}
            {/* ---------------------------------------------------------------- */}
            {loading ? (
                <LoadingSkeleton />
            ) : (
                <div className={styles.content}>

                    {/* -------------------------------------------------------------- */}
                    {/* Alert Banner                                                    */}
                    {/* -------------------------------------------------------------- */}
                    <div className={styles.alertBanner}>
                        ⚠ <strong>ACTIVE CONFLICT:</strong> Houthis opened 2nd front at Bab al-Mandeb on Mar 30.
                        Brent hits $114.88. ~2,000 ships stranded. Trump threatens Iranian energy infrastructure.
                        Hormuz passage at 4.4% of pre-war capacity.
                    </div>

                    {/* -------------------------------------------------------------- */}
                    {/* KPI Cards                                                       */}
                    {/* -------------------------------------------------------------- */}
                    <div className={styles.kpiGrid}>
                        {KPI_CARDS.map((kpi) => (
                            <div
                                key={kpi.label}
                                className={styles.kpiCard}
                                style={{ borderLeftColor: kpi.accent }}
                            >
                                <p className={styles.kpiLabel}>
                                    {kpi.pulse && <span className={styles.pulseDot} />}
                                    {kpi.label}
                                </p>
                                <p className={styles.kpiValue}>{kpi.value}</p>
                                <p className={styles.kpiSub}>{kpi.sub}</p>
                            </div>
                        ))}
                    </div>

                    {/* -------------------------------------------------------------- */}
                    {/* Hormuz Capacity Bar                                             */}
                    {/* -------------------------------------------------------------- */}
                    <div className={styles.capacitySection}>
                        <p className={styles.capacityLabel}>
                            Hormuz Passage Capacity — {capacityPct.toFixed(1)}% of pre-war
                        </p>
                        <div className={styles.capacityBarOuter}>
                            <div
                                className={styles.capacityBarInner}
                                style={{ width: `${capacityPct}%` }}
                            />
                        </div>
                        <div className={styles.capacitySubLabels}>
                            <span>{vesselsPerDay} vessels/day</span>
                            <span>Normal: 135/day</span>
                        </div>
                    </div>

                    {/* -------------------------------------------------------------- */}
                    {/* Charts Row                                                      */}
                    {/* -------------------------------------------------------------- */}
                    <div className={styles.chartsRow}>
                        {/* Chart 1 — Brent Price History */}
                        <div className={styles.chartCard}>
                            <p className={styles.chartTitle}>Brent Crude (USD/bbl)</p>
                            <ResponsiveContainer width="100%" height={280}>
                                <AreaChart
                                    data={priceChartData}
                                    margin={{ top: 8, right: 16, left: 0, bottom: 32 }}
                                    style={{ fontFamily: 'inherit' }}
                                >
                                    <defs>
                                        <linearGradient id="brentGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fill: '#6b7280', fontSize: 11 }}
                                        angle={-45}
                                        textAnchor="end"
                                        interval={4}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        domain={[65, 125]}
                                        tick={{ fill: '#6b7280', fontSize: 11 }}
                                        tickFormatter={(v) => `$${v}`}
                                        tickLine={false}
                                        axisLine={false}
                                        width={48}
                                    />
                                    <Tooltip content={<BrentTooltip />} />
                                    {/* War start reference line */}
                                    <ReferenceLine
                                        x="Feb 28"
                                        stroke="#ef4444"
                                        strokeDasharray="4 4"
                                        label={{ value: 'War Start', fill: '#ef4444', fontSize: 10, position: 'top' }}
                                    />
                                    {/* $100 reference line */}
                                    <ReferenceLine
                                        y={100}
                                        stroke="#6b7280"
                                        strokeDasharray="3 3"
                                        label={{ value: '$100', fill: '#6b7280', fontSize: 10, position: 'right' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="price"
                                        stroke="#f59e0b"
                                        strokeWidth={2}
                                        fill="url(#brentGradient)"
                                        dot={false}
                                        activeDot={{ r: 5, fill: '#f59e0b', stroke: '#111827', strokeWidth: 2 }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Chart 2 — Tanker Traffic */}
                        <div className={styles.chartCard}>
                            <p className={styles.chartTitle}>Strait of Hormuz Traffic (vessels/day)</p>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart
                                    data={FALLBACK_TRAFFIC_DATA}
                                    margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                                    style={{ fontFamily: 'inherit' }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                                    <XAxis
                                        dataKey="period"
                                        tick={{ fill: '#6b7280', fontSize: 10 }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        domain={[0, 145]}
                                        tick={{ fill: '#6b7280', fontSize: 11 }}
                                        tickLine={false}
                                        axisLine={false}
                                        label={{
                                            value: 'vessels/day',
                                            angle: -90,
                                            position: 'insideLeft',
                                            fill: '#6b7280',
                                            fontSize: 11,
                                            dy: 40,
                                        }}
                                        width={56}
                                    />
                                    <Tooltip content={<TrafficTooltip />} />
                                    <Bar dataKey="vessels" radius={[3, 3, 0, 0]}>
                                        {FALLBACK_TRAFFIC_DATA.map((entry, i) => (
                                            <Cell
                                                key={`cell-${i}`}
                                                fill={getBarColor(entry.status)}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* -------------------------------------------------------------- */}
                    {/* Global Supply Panel                                             */}
                    {/* -------------------------------------------------------------- */}
                    <div className={styles.supplyPanel}>
                        <p className={styles.supplyPanelTitle}>
                            Strait of Hormuz — Global Supply at Risk
                        </p>
                        <div className={styles.supplyGrid}>
                            {[
                                { value: '20%', label: 'of global crude oil supply' },
                                { value: '20%', label: 'of global LNG trade' },
                                { value: '18%', label: 'of global LPG' },
                                { value: '3%', label: 'of global refined products' },
                            ].map((item) => (
                                <div key={item.label} className={styles.supplyBox}>
                                    <div className={styles.supplyValue}>{item.value}</div>
                                    <div className={styles.supplyBoxLabel}>{item.label}</div>
                                </div>
                            ))}
                        </div>
                        <p className={styles.globalNote}>
                            Pre-war baseline. Current blockade affects all of the above.
                        </p>
                    </div>

                    {/* -------------------------------------------------------------- */}
                    {/* Daily Intelligence Digest                                       */}
                    {/* -------------------------------------------------------------- */}
                    {dailyDigest.length > 0 ? (
                        <div className={styles.newsSection}>
                            <p className={styles.newsSectionTitle}>Daily Intelligence Digest</p>
                            {dailyDigest.map((day) => (
                                <div key={day.date} className={styles.digestItem}>
                                    <div className={styles.newsMeta}>
                                        <span
                                            className={styles.severityBadge}
                                            style={getSeverityStyle(day.severity)}
                                        >
                                            {day.severity}
                                        </span>
                                        <span className={styles.newsDate}>
                                            {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric',
                                            })}
                                        </span>
                                        <span className={styles.digestArticleCount}>
                                            {day.article_count} source{day.article_count !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <p className={styles.newsTitle}>{day.title}</p>
                                    <p className={styles.newsAnalysis}>{day.summary}</p>
                                    {day.market_impact && (
                                        <p className={styles.newsMarketImpact}>
                                            <strong>Market Impact:</strong> {day.market_impact}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : latestNews.length > 0 && (
                        <div className={styles.newsSection}>
                            <p className={styles.newsSectionTitle}>Latest News</p>
                            {latestNews.slice(0, 5).map((item, i) => (
                                <div key={item.url || i} className={styles.newsItem}>
                                    <div className={styles.newsMeta}>
                                        <span className={styles.newsSource}>{item.source}</span>
                                        {item.severity && (
                                            <span
                                                className={styles.severityBadge}
                                                style={getSeverityStyle(item.severity)}
                                            >
                                                {item.severity}
                                            </span>
                                        )}
                                        <span className={styles.newsDate}>
                                            {item.publishedAt
                                                ? new Date(item.publishedAt).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                })
                                                : ''}
                                        </span>
                                    </div>
                                    <p className={styles.newsTitle}>{item.title}</p>
                                    {item.description && (
                                        <p className={styles.newsDesc}>{item.description}</p>
                                    )}
                                    <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.newsLink}
                                    >
                                        Read Full Article →
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* -------------------------------------------------------------- */}
                    {/* War Events Timeline                                             */}
                    {/* -------------------------------------------------------------- */}
                    <div className={styles.timelineSection}>
                        <p className={styles.timelineSectionTitle}>War Events Timeline</p>

                        {/* Filter Bar */}
                        <div className={styles.filterBar}>
                            {FILTER_OPTIONS.map((f) => (
                                <button
                                    key={f}
                                    className={`${styles.filterBtn} ${activeFilter === f ? styles.filterBtnActive : ''}`}
                                    onClick={() => setActiveFilter(f)}
                                >
                                    {f.charAt(0).toUpperCase() + f.slice(1)}
                                </button>
                            ))}
                        </div>

                        {/* Event List */}
                        <div className={styles.timelineList}>
                            {filteredEvents.map((event, i) => (
                                <div key={`${event.date}-${i}`} className={styles.timelineRow}>
                                    <span className={styles.timelineDate}>{event.date}</span>
                                    <span>
                                        <span
                                            className={styles.severityBadge}
                                            style={getSeverityStyle(event.severity)}
                                        >
                                            {event.severity}
                                        </span>
                                    </span>
                                    <div className={styles.timelineText}>
                                        <p className={styles.timelineEventTitle}>{event.title}</p>
                                        <p className={styles.timelineEventDesc}>{event.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* -------------------------------------------------------------------- */}
            {/* Footer Sources                                                        */}
            {/* -------------------------------------------------------------------- */}
            <div className={styles.footerSources}>
                <p className={styles.footerSourcesTitle}>Sources</p>
                <ul className={styles.footerSourcesList}>
                    <li>
                        <a href="https://www.cnbc.com/2026/03/30/oil-price-today-wti-brent-yemen-houthis-israel-iran-war.html" target="_blank" rel="noopener noreferrer">
                            CNBC — Oil price Mar 30
                        </a>
                    </li>
                    <li>
                        <a href="https://news.usni.org/2026/03/27/irgc-opens-tolled-passage-for-merchant-ships-in-strait-of-hormuz-transit-continues-to-trickle-through" target="_blank" rel="noopener noreferrer">
                            USNI News — IRGC tolled passage
                        </a>
                    </li>
                    <li>
                        <a href="https://www.aljazeera.com/news/2026/3/26/tehranstollbooth-how-iran-picks-who-to-let-through-strait-of-hormuz" target="_blank" rel="noopener noreferrer">
                            Al Jazeera — Tehran's toll booth
                        </a>
                    </li>
                    <li>
                        <a href="https://www.insurancejournal.com/news/international/2026/03/30/863873.htm" target="_blank" rel="noopener noreferrer">
                            Insurance Journal — War risk cover
                        </a>
                    </li>
                    <li>
                        <a href="https://en.wikipedia.org/wiki/Timeline_of_the_2026_Iran_war" target="_blank" rel="noopener noreferrer">
                            Wikipedia — 2026 Iran war timeline
                        </a>
                    </li>
                    <li>
                        <a href="https://www.reuters.com/business/aerospace-defense/top-us-general-outlines-initial-timeline-us-military-operation-iran-2026-03-02/" target="_blank" rel="noopener noreferrer">
                            Reuters — US military timeline
                        </a>
                    </li>
                    <li>
                        <a href="https://apnews.com/article/iran-us-israel-trump-lebanon-march-30-2026-8abb0ee50be4cd8dd9ddde3a9d846ef8" target="_blank" rel="noopener noreferrer">
                            AP News — Iran–US Mar 30
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default HormuzWatchPage;
